/**
 * Safe Datadog RUM wrapper that only loads @datadog/browser-rum when initialized.
 *
 * Usage:
 *   import { initDatadog, addError, setUser, clearUser, setUserProperty, setGlobalContextProperty } from 'tio-common/utils/datadog';
 *
 * In application route (no await needed — calls made while the SDK loads are queued and
 * replayed once it is ready):
 *   if (ENV.datadog) { initDatadog(ENV.datadog).catch(...); }
 *
 * Everywhere else — just call methods directly (they no-op when init was never started):
 *   addError('something broke', { context });
 *   setUser({ id: user.id, name: user.name });
 */
import type { RumInitConfiguration } from '@datadog/browser-rum';

type DatadogRum = typeof import('@datadog/browser-rum').datadogRum;
/** Loads the RUM SDK module; injectable so tests can stand in a fake without touching the network. */
export type RumSdkLoader = () => Promise<{ datadogRum: DatadogRum }>;

let rum: DatadogRum | null = null;
let initStarted = false;
let queued: Array<(sdk: DatadogRum) => void> = [];

/** Runs `call` now when the SDK is ready, or once `initDatadog` finishes; dropped when init was never started. */
function withRum(call: (sdk: DatadogRum) => void) {
  if (rum) call(rum);
  else if (initStarted) queued.push(call);
}

/**
 * Loads and initializes the RUM SDK. Calls to the helpers below made while this is in
 * flight are queued and replayed in order once the SDK is ready.
 *
 * @param config RUM init configuration for the current environment.
 * @param load how to obtain the SDK module; defaults to the real dynamic import.
 * @throws when the SDK bundle fails to load or `init` throws; queued calls are dropped and
 *   later calls are dropped too, as if init had never been started.
 */
export async function initDatadog(
  config: RumInitConfiguration,
  load: RumSdkLoader = () => import('@datadog/browser-rum')
) {
  initStarted = true;
  try {
    const { datadogRum } = await load();
    datadogRum.init(config);
    rum = datadogRum;
    for (const call of queued) call(datadogRum);
  } catch (error) {
    initStarted = false;
    throw error;
  } finally {
    queued = [];
  }
}

/** Forgets the initialized SDK and any queued calls; for tests that initialize more than once. */
export function resetDatadog() {
  rum = null;
  initStarted = false;
  queued = [];
}

export function addError(...args: Parameters<DatadogRum['addError']>) {
  withRum((sdk) => sdk.addError(...args));
}

export function setUser(...args: Parameters<DatadogRum['setUser']>) {
  withRum((sdk) => sdk.setUser(...args));
}

export function clearUser() {
  withRum((sdk) => sdk.clearUser());
}

export function setUserProperty(...args: Parameters<DatadogRum['setUserProperty']>) {
  withRum((sdk) => sdk.setUserProperty(...args));
}

export function setGlobalContextProperty(
  ...args: Parameters<DatadogRum['setGlobalContextProperty']>
) {
  withRum((sdk) => sdk.setGlobalContextProperty(...args));
}
